import { BrebKeyType } from "../services/paymentsPayout.service";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ALIAS = /^@[a-zA-Z0-9._-]{3,63}$/;
const DIGITS = /^\d{6,20}$/;
const PLAIN = /^(?=.*[a-zA-Z])[a-zA-Z0-9]{5,64}$/;
const FORMATTED_NUMBER = /^\+?[\d\s.()-]+$/;

/**
 * Whether a key, exactly as written, is worth asking the directory about. Keys are unique, so a value is
 * never rewritten to fit: dropping a `57`, a separator or a capital could ask about, and pay, someone else.
 */
export function looksLikeBrebKey(value: string): boolean {
  return EMAIL.test(value) || ALIAS.test(value) || DIGITS.test(value) || PLAIN.test(value);
}

/** A number written with spaces, dots, dashes or a `+`, or a space anywhere: the sender must type it clean. */
export function brebKeyHasSeparators(raw: string): boolean {
  const value = raw.trim();
  return /\s/.test(value) || (FORMATTED_NUMBER.test(value) && !/^\d+$/.test(value));
}

/** A guess from the value's shape, for when the directory answers without a key type. Never MERCHANT. */
export function inferBrebKeyType(value: string): BrebKeyType {
  const v = value.trim();
  if (EMAIL.test(v)) return BrebKeyType.EMAIL;
  if (v.startsWith("@")) return BrebKeyType.ALPHANUM;
  const digits = v.replace(/\D/g, "");
  if (digits.length === v.length && /^3\d{9}$/.test(digits)) return BrebKeyType.PHONE;
  if (/^\d{5,20}$/.test(v)) return BrebKeyType.NRIC;
  return BrebKeyType.ALPHANUM;
}

/** The directory's own key type when it is one the payout accepts, the shape's guess otherwise. */
export function resolvedBrebKeyType(directoryKeyType: string | null | undefined, value: string): BrebKeyType {
  const reported = String(directoryKeyType ?? "")
    .trim()
    .toUpperCase();
  return (Object.values(BrebKeyType) as string[]).includes(reported)
    ? (reported as BrebKeyType)
    : inferBrebKeyType(value);
}

/** The key as the sender typed it, readable: a mobile grouped 3-3-4, anything else untouched. */
export function displayBrebKey(type: BrebKeyType, value: string): string {
  const digits = value.replace(/\D/g, "");
  if (type === BrebKeyType.PHONE && /^3\d{9}$/.test(digits)) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  return value.trim();
}
